/**
 * Resolving backend paragraphIds to live Word paragraph proxies.
 *
 * Split out of `wordListStructureOperations` because the multi-id form is a
 * correctness constraint rather than a convenience: see
 * `resolveParagraphsByIndex` below.
 */

import type { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";

/** A live paragraph resolved from a backend paragraphId. */
export interface ResolvedParagraph {
  paragraph: Word.Paragraph | null;
  adjustedIndex: number;
  outOfBounds: boolean;
}

/**
 * Resolve several backend paragraphIds from a SINGLE `body.paragraphs` load.
 *
 * Loading the collection a second time while holding a paragraph proxy from the
 * first load invalidates that proxy's reference id — Word for Mac then fails the
 * next sync with "GeneralException at Document._GetObjectByReferenceId", while
 * Word Online tolerates it. Every caller that needs two paragraphs (the
 * `continueList` target and its anchor) must therefore resolve them together.
 */
export const resolveParagraphsByIndex = async (
  context: Word.RequestContext,
  paragraphIds: number[],
  indexTracker?: UnifiedIndexTracker
): Promise<ResolvedParagraph[]> => {
  const adjusted = paragraphIds.map((id) =>
    indexTracker ? indexTracker.calculateAdjustedIndexForEdit(id) : id
  );

  const paragraphs = context.document.body.paragraphs;

  paragraphs.load("items");
  await context.sync();

  return adjusted.map((adjustedIndex) =>
    adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length
      ? { paragraph: null, adjustedIndex, outOfBounds: true }
      : { paragraph: paragraphs.items[adjustedIndex], adjustedIndex, outOfBounds: false }
  );
};

/**
 * Single-paragraph resolution. Mirrors the resolution used by
 * `readLiveParagraphText` and the INSERT/DELETE paths.
 */
export const resolveParagraphByIndex = async (
  context: Word.RequestContext,
  paragraphId: number,
  indexTracker?: UnifiedIndexTracker
): Promise<ResolvedParagraph> => {
  const [resolved] = await resolveParagraphsByIndex(context, [paragraphId], indexTracker);

  return resolved;
};
